'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useEscapeKey } from '../../hooks/useEscapeKey'

import { Box, chakra } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import type { IncidentCardPriority } from '../Card/IncidentCard.styles'
import { ChevronDownIcon } from '../Icon'
import { IncidentPriorityIcon } from '../Icon/IncidentPriorityIcon'
import {
  priorityDropdownChevronStyles,
  priorityDropdownItemStyles,
  priorityDropdownMenuStyles,
  priorityDropdownTriggerStyles,
  priorityDropdownWrapperStyles,
} from './PriorityDropdown.styles'

export type PriorityOption = {
  label: string
  value: IncidentCardPriority
}

const DEFAULT_OPTIONS: PriorityOption[] = [
  // ENG-32095: the 'urgent' card-priority value maps to IncidentPriority.Critical,
  // and every other surface labels it "Critical" — keep the display name aligned.
  { label: 'Critical', value: 'urgent' },
  { label: 'High', value: 'high' },
  { label: 'Medium', value: 'medium' },
  { label: 'Low', value: 'low' },
  { label: 'None', value: 'none' },
]

export type PriorityDropdownProps = {
  priority: IncidentCardPriority
  onPriorityChange: (priority: IncidentCardPriority) => void
  options?: PriorityOption[]
  isDisabled?: boolean
  colorScheme?: ColorScheme
}

export const PriorityDropdown = ({
  priority,
  onPriorityChange,
  options = DEFAULT_OPTIONS,
  isDisabled = false,
  colorScheme = 'dark',
}: PriorityDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const handleTriggerClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (isDisabled) return
      setIsOpen((prev) => !prev)
    },
    [isDisabled]
  )

  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const handleEscape = useCallback(() => setIsOpen(false), [])
  useEscapeKey(handleEscape, isOpen)

  const handleSelect = useCallback(
    (value: IncidentCardPriority) => {
      if (value !== priority) {
        onPriorityChange(value)
      }
      setIsOpen(false)
    },
    [priority, onPriorityChange]
  )

  const displayPriority = priority === 'none' ? null : priority

  return (
    <Box ref={wrapperRef} {...priorityDropdownWrapperStyles}>
      <chakra.button
        {...priorityDropdownTriggerStyles(colorScheme)}
        aria-expanded={isOpen}
        aria-haspopup='listbox'
        aria-label='Change priority'
        data-testid='priority-dropdown-trigger'
        disabled={isDisabled}
        onClick={handleTriggerClick}
        type='button'
      >
        <IncidentPriorityIcon
          colorScheme={colorScheme}
          priority={displayPriority}
          size={20}
        />
        <Box {...priorityDropdownChevronStyles(colorScheme)}>
          <ChevronDownIcon height={8} width={8} />
        </Box>
      </chakra.button>
      {isOpen && (
        <Box {...priorityDropdownMenuStyles(colorScheme)} role='listbox'>
          {options.map((option) => (
            <Box
              {...priorityDropdownItemStyles(colorScheme)}
              aria-selected={option.value === priority}
              data-testid={`priority-option-${option.value}`}
              key={option.value}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                handleSelect(option.value)
              }}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  handleSelect(option.value)
                }
              }}
              role='option'
              tabIndex={0}
            >
              <IncidentPriorityIcon
                colorScheme={colorScheme}
                priority={option.value}
                size={20}
              />
              {option.label}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}
